import React, { useEffect, useRef, useState } from "react";
import { FileText, Download, Upload, UploadCloud, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import StatusBadge from "../components/StatusBadge";
import { useToast } from "../hooks/use-toast";
import api from "../api";

function Card({ children, className = "" }) {
  return <div className={`rounded-2xl border border-slate-200 bg-white p-5 shadow-sm md:p-6 ${className}`}>{children}</div>;
}

export default function Documents() {
  const { toast } = useToast();
  const [docs, setDocs] = useState([]);
  const [dragging, setDragging] = useState(false);
  const [uploading, setUploading] = useState(null);
  const fileRef = useRef(null);
  const dropRef = useRef(null);
  const activeRef = useRef(null);

  const load = () => api.get("/documents").then((r) => setDocs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const allApproved = docs.length > 0 && docs.every((d) => d.status === "Approved");
  const onFile = docs.filter((d) => d.status !== "Missing").length;
  const total = docs.length;

  const doUpload = async (docId, fileObj) => {
    if (!fileObj) return;
    setUploading(docId);
    try {
      const fd = new FormData();
      fd.append("file", fileObj);
      const res = await api.post(`/documents/${docId}/upload`, fd);
      setDocs(res.data);
      const name = docs.find((d) => d.id === docId)?.name || "Document";
      toast({ title: "Upload complete", description: `${name} uploaded — pending review.` });
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Upload failed";
      toast({ title: "Upload failed", description: String(detail), variant: "destructive" });
    } finally {
      setUploading(null);
    }
  };

  const pickForRow = (docId) => {
    activeRef.current = docId;
    if (fileRef.current) {
      fileRef.current.value = "";
      fileRef.current.click();
    }
  };

  const onRowFile = (e) => {
    const f = e.target.files?.[0];
    if (f && activeRef.current) doUpload(activeRef.current, f);
    activeRef.current = null;
  };

  const uploadNext = (fileObj) => {
    const target = docs.find((d) => d.status === "Missing") || docs.find((d) => d.status === "Rejected");
    if (!target) {
      toast({ title: "Choose a document", description: "Use the Upload button next to the specific document you want to replace." });
      return;
    }
    doUpload(target.id, fileObj);
  };

  const download = async (docId) => {
    try {
      const res = await api.get(`/documents/${docId}/download`);
      window.open(res.data.url, "_blank", "noopener");
    } catch (err) {
      const detail = err.response?.data?.detail || "Could not download";
      toast({ title: "Download failed", description: String(detail), variant: "destructive" });
    }
  };

  return (
    <div className="space-y-6">
      <input ref={fileRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={onRowFile} />

      <Card>
        <div className="flex items-start justify-between">
          <div>
            <h2 className="text-base font-bold text-slate-900">Required documents</h2>
            <p className="mt-1 text-sm text-slate-500">Each item is on file once you upload it <strong>or</strong> submit the matching onboarding form. Your onboarding team reviews every submission.</p>
          </div>
          <div className="flex flex-col items-end gap-1.5">
            <StatusBadge status={allApproved ? "Approved" : "Approval pending"} />
            <span className="text-xs font-semibold text-slate-500" data-testid="documents-on-file-count">{onFile} of {total} on file</span>
          </div>
        </div>

        <div className="mt-5 space-y-3">
          {docs.map((doc) => (
            <div key={doc.id} className="flex flex-col gap-3 rounded-xl border border-slate-100 p-4 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-100">
                  <FileText className="h-5 w-5 text-slate-500" />
                </div>
                <div>
                  <div className="font-semibold text-slate-900">{doc.name}</div>
                  <div className="mt-1 flex items-center gap-2">
                    <StatusBadge status={doc.status} />
                    {doc.via_form
                      ? <span className="rounded-full border border-violet-200 bg-violet-50 px-2 py-0.5 text-[11px] font-semibold text-violet-700" data-testid={`doc-via-form-${doc.id}`}>via form</span>
                      : doc.filename && <span className="text-xs text-slate-400">{doc.filename}</span>}
                  </div>
                  {doc.review_notes && <div className="mt-1 rounded-md bg-amber-50 px-2 py-1 text-xs text-amber-800" data-testid={`doc-review-note-${doc.id}`}>Reviewer note: {doc.review_notes}</div>}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" className="gap-1.5" disabled={!doc.has_file} onClick={() => download(doc.id)} data-testid={`doc-download-${doc.id}`}>
                  <Download className="h-3.5 w-3.5" /> Download
                </Button>
                <Button size="sm" disabled={uploading === doc.id} onClick={() => pickForRow(doc.id)} data-testid={`doc-upload-${doc.id}`} className="btn-primary-gradient gap-1.5 border-0 text-white">
                  {uploading === doc.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Upload className="h-3.5 w-3.5" />}
                  {doc.status === "Missing" ? "Upload" : "Re-upload"}
                </Button>
              </div>
            </div>
          ))}
        </div>

        <p className="mt-4 text-xs text-slate-400">Applicants can upload documents but cannot approve them. Only supervisors and administrators can approve or reject submissions.</p>
      </Card>

      <Card>
        <h2 className="text-base font-bold text-slate-900">Upload a new document</h2>
        <div
          onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={(e) => { e.preventDefault(); setDragging(false); uploadNext(e.dataTransfer.files?.[0]); }}
          className={`mt-4 flex flex-col items-center justify-center rounded-2xl border-2 border-dashed p-10 text-center transition-colors ${dragging ? "border-violet-400 bg-violet-50" : "border-slate-200"}`}
        >
          <UploadCloud className="h-8 w-8 text-slate-400" />
          <div className="mt-3 font-semibold text-slate-800">Drag and drop your file here</div>
          <div className="mt-1 text-sm text-slate-500">PDF, DOCX, JPG or PNG up to 10 MB — fills your next required document</div>
          <input ref={dropRef} type="file" className="hidden" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png" onChange={(e) => uploadNext(e.target.files?.[0])} />
          <Button onClick={() => dropRef.current?.click()} className="btn-primary-gradient mt-4 border-0 text-white">Choose file</Button>
        </div>
      </Card>
    </div>
  );
}
